// CrazyGames SDK v3 integration
// SDK is loaded via <script> in index.html — window.CrazyGames is the entry point.
// Only active when built with VITE_TARGET=crazygames.

const IS_CRAZYGAMES = import.meta.env.VITE_TARGET === 'crazygames';

function sdk() {
    return window.CrazyGames?.SDK ?? null;
}

export class CrazyGamesIntegration {
    constructor() {
        this.initialized = false;
        this.user = null;
        // Callbacks set by the game to pause/resume when ads run
        this._pauseGame = null;
        this._resumeGame = null;
    }

    // Call once on startup (before gameplay begins).
    async initialize(pauseCallback, resumeCallback) {
        this._pauseGame = pauseCallback;
        this._resumeGame = resumeCallback;

        if (!IS_CRAZYGAMES) {
            return false;
        }

        if (!sdk()) {
            console.log('CrazyGames: SDK not present, skipping');
            return false;
        }

        try {
            await sdk().init();
            this.initialized = true;

            // Listen for platform mute-audio setting
            sdk().game.settings.addEventListener('muteAudio', (muted) => {
                if (muted) this._pauseAudio?.();
                else this._resumeAudio?.();
            });

            // Fetch user (may be null if not logged in)
            try {
                this.user = await sdk().user.getUser();
            } catch (_) { /* not logged in */ }

            console.log('CrazyGames: initialized', this.user ? `as ${this.user.username}` : '(guest)');
            return true;
        } catch (err) {
            console.warn('CrazyGames: init failed', err);
            return false;
        }
    }

    // ── Loading events ────────────────────────────────────────────────────────

    loadingStart() {
        if (IS_CRAZYGAMES) sdk()?.game.loadingStart();
    }

    loadingStop() {
        if (IS_CRAZYGAMES) sdk()?.game.loadingStop();
    }

    // ── Gameplay events ───────────────────────────────────────────────────────

    gameplayStart() {
        if (IS_CRAZYGAMES && this.initialized) sdk()?.game.gameplayStart();
    }

    gameplayStop() {
        if (IS_CRAZYGAMES && this.initialized) sdk()?.game.gameplayStop();
    }

    // Call on major victories/high scores — triggers CrazyGames confetti
    happytime() {
        if (IS_CRAZYGAMES && this.initialized) sdk()?.game.happytime();
    }

    // ── Ads ───────────────────────────────────────────────────────────────────

    // Midgame ad — show between rounds or on game over.
    // The SDK enforces a ~3-minute cooldown automatically.
    showMidgameAd() {
        if (!IS_CRAZYGAMES || !this.initialized) return Promise.resolve();

        return new Promise((resolve) => {
            sdk().ad.requestAd('midgame', {
                adStarted: () => {
                    this._pauseGame?.();
                },
                adFinished: () => {
                    this._resumeGame?.();
                    resolve({ watched: true });
                },
                adError: (err) => {
                    // Non-fatal: unfilled / adblock / cooldown — just resume
                    this._resumeGame?.();
                    resolve({ watched: false, err });
                }
            });
        });
    }

    // Rewarded ad — show when the player opts in (e.g. "watch ad to continue").
    // Resolves { rewarded: true } only when the user watches the full ad.
    showRewardedAd() {
        if (!IS_CRAZYGAMES || !this.initialized) return Promise.resolve({ rewarded: false });

        return new Promise((resolve) => {
            sdk().ad.requestAd('rewarded', {
                adStarted: () => {
                    this._pauseGame?.();
                },
                adFinished: () => {
                    this._resumeGame?.();
                    resolve({ rewarded: true });
                },
                adError: (err) => {
                    this._resumeGame?.();
                    resolve({ rewarded: false, err });
                }
            });
        });
    }

    hasAdblock() {
        return sdk()?.ad.hasAdblock() ?? false;
    }

    // ── User ──────────────────────────────────────────────────────────────────

    getUser() {
        return this.user;
    }

    async showAuthPrompt() {
        if (!this.initialized) return null;
        try {
            this.user = await sdk().user.showAuthPrompt();
            return this.user;
        } catch (_) {
            return null;
        }
    }

    getUserName() {
        return this.user?.username ?? null;
    }

    isAvailable() {
        return this.initialized;
    }
}
